import React, { useEffect, useRef, useState } from 'react';
import { fetchAuditoriaConvites } from '../../../api/portalCotacoes/portalCotacoesApi';
import { AuditoriaConvites, CONVITE_STATUS_LABEL } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
// Mesma folha dos modais de detalhe da Auditoria (Planejamento, Liberação Administrativa).
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import './AuditoriaPropostaModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Fornecedores convidados na cotação (14/08/2026, pedido do Victor: "um menu que detalhe
 * quais fornecedores foram convidados").
 *
 * ⚠️ Mostra o conjunto ATUAL de convites, não uma foto do momento do envio — a edição da
 * cotação pode recriar os convites no backend. Por isso o evento carrega a contagem que o
 * log gravou: divergindo da lista, o modal AVISA, em vez de apresentar o conjunto de hoje
 * como se fosse o de então.
 */

interface AuditoriaConvitesModalProps {
  cotacaoId: string;
  /** Contagem gravada no log do envio; `null` em registro antigo que não a tenha. */
  quantidadeRegistrada: number | null;
  onFechar: () => void;
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

const AuditoriaConvitesModal: React.FC<AuditoriaConvitesModalProps> = ({
  cotacaoId,
  quantidadeRegistrada,
  onFechar,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [dados, setDados] = useState<AuditoriaConvites | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onFechar]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const resposta = await fetchAuditoriaConvites(cotacaoId);
        if (!ativo) return;
        setDados(resposta);
        setErro(null);
      } catch (error) {
        if (!ativo) return;
        // Lista vazia por erro leria como "ninguém foi convidado" — afirmação de negócio
        // tirada de uma falha de rede.
        setDados(null);
        setErro('Não foi possível carregar os fornecedores. Feche e tente de novo; se persistir, acione a TI.');
        notify.errorFrom(error, 'Não foi possível carregar os fornecedores convidados.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [cotacaoId]);

  const total = dados?.convites.length ?? 0;
  const responderam = (dados?.convites ?? []).filter((c) => c.respondidoEm).length;
  const divergiu = dados !== null && quantidadeRegistrada !== null && quantidadeRegistrada !== total;

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Fornecedores Convidados</h2>
          <button className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        {dados && (
          <div className="planning-modal-metadata">
            <div className="metadata-item">
              <strong>Cotação:</strong> <span>{dados.cotacaoNumero}</span>
            </div>
            <div className="metadata-item">
              <strong>Convidados:</strong> <span>{total}</span>
            </div>
            <div className="metadata-item">
              <strong>Responderam:</strong> <span>{responderam}</span>
            </div>
          </div>
        )}

        <div className="planning-modal-body">
          {carregando && <p className="auditoria-proposta-vazio">Carregando os fornecedores...</p>}

          {erro && (
            <p className="auditoria-proposta-erro" role="alert">
              {erro}
            </p>
          )}

          {dados && (
            <>
              {/*
                O conjunto mudou depois do envio: sem este aviso, quem audita leria a lista
                de hoje como se fosse a de quem recebeu o convite naquele dia.
              */}
              {divergiu && (
                <p className="auditoria-proposta-aviso">
                  No envio foram registrados <strong>{quantidadeRegistrada}</strong> fornecedor(es), e hoje a
                  cotação tem <strong>{total}</strong>. A lista abaixo é a atual — a cotação foi editada depois
                  do envio.
                </p>
              )}

              {total === 0 ? (
                <p className="auditoria-proposta-vazio">Nenhum fornecedor convidado nesta cotação.</p>
              ) : (
                <div className="auditoria-proposta-tabela-wrapper">
                  <table className="auditoria-proposta-tabela">
                    <thead>
                      <tr>
                        <th>Fornecedor</th>
                        <th>Situação</th>
                        <th>Canais</th>
                        <th>Convite em</th>
                        <th>Abriu em</th>
                        <th>Respondeu em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.convites.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <div>{c.fornecedorNome}</div>
                            <div className="auditoria-proposta-secundario">{c.fornecedorCnpj}</div>
                          </td>
                          <td>
                            {CONVITE_STATUS_LABEL[c.status] ?? c.status}
                            {c.propostaVersao !== null && (
                              <div className="auditoria-proposta-secundario">versão {c.propostaVersao}</div>
                            )}
                          </td>
                          <td>{c.canais.map((x) => (x === 'EMAIL' ? 'E-mail' : 'WhatsApp')).join(', ')}</td>
                          <td>{dataHora(c.disparadoEm)}</td>
                          <td>{dataHora(c.primeiroAcessoEm)}</td>
                          <td>{dataHora(c.respondidoEm)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="auditoria-proposta-nota">
                Convite por WhatsApp é enviado manualmente pelo comprador — a data de convite registra quando o
                link ficou pronto.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditoriaConvitesModal;
